import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { formatEUR } from '@/lib/revenuePricing';

interface CustomerRow {
  user_id: string;
  user_email: string;
  subs_total: number;
  topups_total: number;
  total: number;
  subs_count: number;
  topups_count: number;
  last_payment_at: string | null;
}

interface TopCustomersTableProps {
  data: CustomerRow[];
}

export function TopCustomersTable({ data }: TopCustomersTableProps) {
  return (
    <Card className="app-tile">
      <CardHeader>
        <CardTitle className="text-lg">Top Customers</CardTitle>
        <CardDescription>Highest revenue contributors in the last 90 days</CardDescription>
      </CardHeader>
      <CardContent>
        {data.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">No revenue in the last 90 days</p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>#</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead className="text-right">Subs</TableHead>
                  <TableHead className="text-right">Top-ups</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead className="whitespace-nowrap">Last payment</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.map((row, idx) => (
                  <TableRow key={row.user_id}>
                    <TableCell className="text-muted-foreground">{idx + 1}</TableCell>
                    <TableCell className="font-medium">{row.user_email}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <span>{formatEUR(row.subs_total)}</span>
                        {row.subs_count > 0 && (
                          <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                            {row.subs_count}
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <span>{formatEUR(row.topups_total)}</span>
                        {row.topups_count > 0 && (
                          <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                            {row.topups_count}
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-semibold">{formatEUR(row.total)}</TableCell>
                    <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                      {row.last_payment_at
                        ? new Date(row.last_payment_at).toLocaleDateString('de-DE')
                        : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
